import { useEffect, useMemo, useRef, useState } from 'react';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import type { PetitionDetailsPageUserData } from '#src/app/pages/petitionDetails/pageContext';
import { treatExperiment } from '#src/app/shared/api/experiments';

type Experiment = NonNullable<PetitionDetailsPageUserData['experiments']['spikingPetitionBanner']>['experiment'];

type Options = Readonly<{
	// True once the banner is selected and not dismissed. Treatment never starts before that.
	enabled: boolean;
}>;

/**
 * Treats the experiment once `enabled`, and returns it with `treated: true` once the treatment is
 * registered. Returns undefined until then, and stays undefined when `treatExperiment` resolves `false`
 * (e.g. a bot session), so no banner or `$exposure` shows for an assignment that was never recorded.
 *
 * A QA `?exo.` override is never treated, since that would register a real assignment, but it is
 * returned with `treated: true`: `useTrackExperimentExposure` checks `treated` before `override`, so
 * otherwise its "$exposure SUPPRESSED by override" log could never show. That branch does not send.
 */
export function useTreatSpikingPetitionBannerExperiment(
	experiment: Experiment,
	{ enabled }: Options,
): Experiment | undefined {
	const utilityContext = useUtilityContext();
	const { experimentName, override } = experiment;
	const [treatedExperimentName, setTreatedExperimentName] = useState<string | null>(null);
	// Ref, not state, so a re-run in the same tick (or `enabled` toggling) can't send a second request.
	const treatRequestedForRef = useRef<string | null>(null);

	useEffect(() => {
		if (!enabled || override || treatRequestedForRef.current === experimentName) return;
		treatRequestedForRef.current = experimentName;
		void (async () => {
			if (await treatExperiment(utilityContext, experimentName)) setTreatedExperimentName(experimentName);
		})();
	}, [enabled, override, experimentName, utilityContext]);

	return useMemo(() => {
		if (!enabled) return undefined;
		if (override || treatedExperimentName === experimentName) return { ...experiment, treated: true };
		return undefined;
	}, [enabled, override, treatedExperimentName, experimentName, experiment]);
}
